<script setup>
import { RouterLink, useRouter } from "vue-router";

import { useAjaxForm } from "@/utils/form";
import { useStore } from "@/store";
import { useToast } from "@/plugins/toast";

import FormInput from "@/components/Form/v1/FormInput.vue";
import FormButton from "@/components/Form/v1/FormButton.vue";

const store = useStore();
const toast = useToast();
const router = useRouter();
const form = useAjaxForm({
    name: store.state.user.name,
    username: '',
    gender: '',
    date_of_birth: '',
    type: '',
    type_details: {
        batch: "",
        batch_stream: "",
        batch_year: "",
        batch_state: "",
    },
    avatar: "",
    bio: "",
    school: "",
    referral_source: "",
    referral_source_other: "",
});

form.onSuccess((response) => {
    store.commit("user", response.data.data);
    store.saveUser();
    toast.publish({
        title: "Profile created successfully, cheers!!!",
        color: "success",
    });
    router.replace({ name: "feed" });
});
</script>

<template>
    <layout-main>
        <head-title value="Onboarding" />
        <layout-content>
            <content-header>
                <top-nav>
                    <top-nav-title> Create your profile </top-nav-title>

                    <RouterLink
                        class="block ml-auto font-semibold text-primary border-primary border rounded-full px-4 py-1"
                        :to="{ name: 'logout' }"
                    >
                        Logout
                    </RouterLink>
                </top-nav>
            </content-header>

            <content-body>
                <div class="p-4">
                    <p>
                        Welcome to Corperland
                        <span class="font-semibold">{{
                            $store.state.user.name
                        }}</span
                        >! Let's help you create your profile to get started.
                    </p>

                    <form
                        class="mt-8"
                        @submit.prevent="form.post('/api/profiles')"
                    >
                        <FormInput
                            :form="form"
                            as="avatar"
                            name="avatar"
                            :input-bind="{
                                roundedFull: true,
                                ariaLabel: 'Avatar',
                                size: '3xl',
                            }"
                        />
                        <FormInput label="Name" name="name" :form="form" />
                        <FormInput
                            lowercase
                            label="Username"
                            name="username"
                            :form="form"
                            help="You can change this only after 30days"
                        />
                        <FormInput
                            :form="form"
                            as="select"
                            name="gender"
                            label="Gender"
                            placeholder="Select your gender"
                            required
                            :input-bind="{
                                options: store.state.app.gender_options,
                            }"
                        />
                        <FormInput label="Date of Birth" name="date_of_birth" as="datepicker" placeholder="YYYY-MM-DD" :form="form" v-model="form.date_of_birth"  />
                        <FormInput :form="form" as="select" name="school" label="School" placeholder="Select your school"
                            required :input-bind="{
                            options: store.state.app.school_options
                        }" />
                        <FormInput :form="form" as="textarea" name="bio" label="Bio" placeholder="Tell us about yourself" />
                        <FormInput
                            :form="form"
                            as="select"
                            name="type"
                            label="Which profile type best suits you?"
                            placeholder="Select One"
                            required
                            :input-bind="{
                                options: store.state.app.profile_type_options,
                            }"
                        />
                        <FormInput
                            v-if="form.type == 1 || form.type == 3"
                            :form="form"
                            as="input"
                            name="type_details.batch_year"
                            label="Year of Deployment"
                            placeholder="2xxx"
                            v-model="form.type_details.batch_year"
                            :input-bind="{
                                min: 1960,
                                max: 2024,
                                type: 'number',
                            }"
                        />
                        <FormInput
                            v-if="form.type == 1 || form.type == 3"
                            :form="form"
                            v-model="form.type_details.batch"
                            as="select"
                            name="type_details.batch"
                            label="Batch"
                            placeholder="Select Batch"
                            required
                            :input-bind="{
                                options: {
                                    A: 'A',
                                    B: 'B',
                                    C: 'C',
                                },
                            }"
                        />
                        <FormInput
                            v-if="form.type == 1 || form.type == 3"
                            :form="form"
                            v-model="form.type_details.batch_stream"
                            as="select"
                            name="type_details.batch_stream"
                            label="Stream"
                            placeholder="Select Stream"
                            required
                            :input-bind="{
                                options: {
                                    I: 'I',
                                    II: 'II',
                                    III: 'III',
                                },
                            }"
                        />
                        <FormInput
                            v-if="form.type == 1 || form.type == 3"
                            :form="form"
                            v-model="form.type_details.batch_state"
                            as="select"
                            name="type_details.batch_state"
                            label="State of Deployment"
                            placeholder="Select State Of Deployment"
                            required
                            :input-bind="{
                                options: store.state.app.location_options,
                            }"
                        />
                        <FormInput
                            :form="form"
                            v-model="form.referral_source"
                            as="select"
                            name="referral_source"
                            label="How did you hear about us?"
                            placeholder="Select One"
                            required
                            :input-bind="{
                                options: {
                                    'Friend or colleague': 'Friend or colleague',
                                    'Social media': 'Social media',
                                    'Instagram': 'Instagram',
                                    'Twitter/X': 'Twitter/X',
                                    'Facebook': 'Facebook',
                                    'TikTok': 'TikTok',
                                    'NYSC Orientation Camp': 'NYSC Orientation Camp',
                                    'NYSC WhatsApp/Telegram Group': 'NYSC WhatsApp/Telegram Group',
                                    'Corperland Ambassador': 'Corperland Ambassador',
                                    'Email newsletter or campaign': 'Email newsletter or campaign',
                                    'Google/Search Engine': 'Google/Search Engine',
                                    'Event or conference': 'Event or conference',
                                    'Marketplace or Community post': 'Marketplace or Community post',
                                    'Other': 'Other (please specify)',
                                },
                            }"
                        />

                        <FormInput
                            v-if="form.referral_source === 'Other'"
                            label="Other (Please specify)"
                            name="referral_source_other"
                            :form="form"
                            placeholder="Type how you heard about us"
                            required
                        />

                        <div class="mt-10">
                            <FormButton :form="form" full color="primary">
                                Create Profile
                            </FormButton>
                        </div>
                    </form>
                </div>
            </content-body>
        </layout-content>
    </layout-main>
</template>
